import React from "react";
import { cn } from "@/utils/utils";
import { useTranslation } from "react-i18next";
import { EntityType } from "@/services/hooks/entities/useEntityType";
import Text, { TextVariant } from "@/components/ui/text";
import TextWithHoverAction, {
  ValueProps
} from "@/components/controls/TextWithHoverAction";
import { getEnumTKey } from "@/utils/strings";
import NewQuickView from "@/components/layouts/NewQuickView/NewQuickView";
import { DrawerStateItem } from "@/services/hooks/entities/useQuickViewDrawer";
import { useNewQuickViewDrawer } from "@/services/hooks/entities/useNewQuickViewDrawer";
import { EntityData } from "@/services/api/types";

const TRANSLATION_KEY = "detailedView";

interface BaseProps {
  linkedEntityType: EntityType;
  linkedEntity: DrawerStateItem;
  onUpdate?: (updatedEntity: EntityData) => void;
  textVariant?: TextVariant;
  className?: string;
  readOnly?: boolean;
}

// A linked value is usually text, but a linked file field's value is a list of
// file chips the caller renders — the row and its edit action are the same either
// way, so the value itself is a slot. The row it forwards to owns that contract,
// so the union comes from there rather than being restated here.
type Props = BaseProps & ValueProps;

const TextWithEditLink: React.FC<Props> = ({
  linkedEntityType,
  linkedEntity,
  onUpdate,
  textVariant = "body",
  className,
  readOnly = false,
  ...valueProps
}) => {
  const { t } = useTranslation();
  const {
    drawerState: newDrawerState,
    openDrawer: newOpenDrawer,
    closeDrawer: newCloseDrawer
  } = useNewQuickViewDrawer();

  const entityId = linkedEntity?.id;

  const handleOpenDrawer = () => {
    if (linkedEntity) {
      newOpenDrawer(linkedEntity.id, linkedEntityType);
    }
  };

  if (readOnly) {
    return (
      <div
        className={cn("px-2 py-2 min-h-9 flex items-center", className)}
      >
        {valueProps.content !== undefined ? (
          valueProps.content
        ) : (
          <Text variant={textVariant}>{valueProps.text}</Text>
        )}
      </div>
    );
  }

  return (
    <>
      <TextWithHoverAction
        {...valueProps}
        actionLabel={t(`${TRANSLATION_KEY}.editIn`, {
          entity: t(
            getEnumTKey("entities", "names", EntityType, linkedEntityType)
          )
        })}
        // The redesigned row shows the entity name alone; the accessible name
        // stays the full "Edit in {Entity}" phrase.
        actionCompactLabel={t(
          getEnumTKey("entities", "names", EntityType, linkedEntityType)
        )}
        actionAriaLabel={t(`${TRANSLATION_KEY}.editIn`, {
          entity: t(
            getEnumTKey("entities", "names", EntityType, linkedEntityType)
          )
        })}
        onAction={entityId ? handleOpenDrawer : undefined}
        textVariant={textVariant}
        className={className}
      />
      <NewQuickView
        isOpen={newDrawerState.isOpen}
        entityId={newDrawerState.entityId}
        entityType={linkedEntityType}
        onClose={newCloseDrawer}
        onEntityUpdate={onUpdate}
      />
    </>
  );
};

export default TextWithEditLink;
